import React, { useState } from 'react'
// Direct module imports, not the barrel — index.ts re-exports this component,
// so importing from './index' here would be a cycle.
import { Badge } from './Badge'
import { Button } from './Button'
import { Panel } from './Panel'
import {
  formatToPacificTime,
  formatToPacificTimeWithSeconds,
} from '../utils/timezone'
import {
  VodPipeline,
  VodPipelineEvent,
  VodPipelinePackaging,
  VodPipelineUpload,
  VodPipelineVersion,
} from '../types/vod'
import styles from './VodPipelinePanel.module.scss'

// Every VOD stack lives in us-east-1 (infra/apps/vod/*/us-east-1).
const MEDIACONVERT_REGION = 'us-east-1'

type BadgeVariant = 'success' | 'warning' | 'error' | 'neutral' | 'secondary'

// Upload failure_code values are internal enum names. Editors read this column,
// so translate; unknown codes fall through to the raw value rather than hiding
// that something failed.
const FAILURE_TEXT: Record<string, string> = {
  SIZE_MISMATCH: 'Wrong file size',
  NOT_MP4: 'Not an MP4',
  UNPARSEABLE: 'Unreadable file',
  NO_VIDEO_STREAM: 'No video track',
  EXPIRED: 'Upload expired',
}

interface MasterLabel {
  text: string
  variant: BadgeVariant
  spin?: boolean
}

/**
 * The master lane, labelled by provenance. VALIDATING means two different
 * things: ffprobe on a browser upload (seconds), but the server-side copy on a
 * registered master (minutes on a large file) — the claim happens before the
 * copy. Labelling both "Checking file" makes every large registration look hung.
 */
function masterLabel(upload: VodPipelineUpload | null): MasterLabel {
  if (!upload) return { text: 'No master', variant: 'neutral' }
  const registered = Boolean(upload.source_vendor)
  switch (upload.state) {
    case 'UPLOADING':
      return { text: 'Uploading', variant: 'secondary', spin: true }
    case 'VALIDATING':
      return registered
        ? { text: 'Copying from S3', variant: 'secondary', spin: true }
        : { text: 'Checking file', variant: 'secondary', spin: true }
    case 'COMPLETED':
      return registered
        ? { text: 'Delivered', variant: 'success' }
        : { text: 'Uploaded', variant: 'success' }
    case 'FAILED':
      return {
        text: upload.failure_code
          ? (FAILURE_TEXT[upload.failure_code] ?? upload.failure_code)
          : 'Failed',
        variant: 'error',
      }
    case 'ABORTED':
      return { text: 'Aborted', variant: 'neutral' }
    default:
      return { text: upload.state, variant: 'neutral' }
  }
}

function packagingLabel(
  packaging: VodPipelinePackaging | null,
  isServing: boolean
): MasterLabel {
  if (!packaging) return { text: 'Never started', variant: 'neutral' }
  if (packaging.awaiting_reconcile) {
    // Ambiguous CreateJob: the job may exist. Never "failed", never a spinner
    // implying our work is in flight.
    return { text: 'Confirming with MediaConvert', variant: 'warning' }
  }
  switch (packaging.state) {
    case 'PENDING':
      // Nothing is happening yet — the lane's next scheduled pass picks it up.
      return { text: 'Queued for packaging', variant: 'warning' }
    case 'SUBMITTED':
    case 'PROGRESSING':
      return { text: 'Encoding', variant: 'secondary', spin: true }
    case 'COMPLETE':
      if (packaging.outputs_deleted) return { text: 'Outputs deleted', variant: 'neutral' }
      if (packaging.outputs_retire_at) {
        // Only the serving version is live. delete_after is stamped when a newer
        // completion supersedes this one, so calling every retiring version
        // "Live" claims the player is still using outputs it has moved off.
        return {
          text: isServing ? 'Live · retiring' : 'Superseded · retiring',
          variant: 'neutral',
        }
      }
      return { text: isServing ? 'Playable' : 'Complete', variant: 'success' }
    case 'ERROR':
      return { text: 'Encoding failed', variant: 'error' }
    case 'CANCELED':
      return { text: 'Superseded before packaging', variant: 'neutral' }
    default:
      return { text: packaging.state, variant: 'neutral' }
  }
}

function provenanceLabel(upload: VodPipelineUpload | null): string {
  if (!upload) return 'Unknown source'
  if (!upload.source_vendor) {
    return upload.created_by
      ? `Uploaded by ${upload.created_by}`
      : 'First-party upload'
  }
  // provenance is the normalised form; source_vendor is stored as written, so
  // "wsc" would otherwise render lowercase mid-sentence.
  return `Delivered by ${upload.provenance}`
}

function bytes(n: number | null | undefined): string {
  if (!n) return '—'
  const gb = n / 1024 ** 3
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  return `${(n / 1024 ** 2).toFixed(0)} MB`
}

function probeSummary(probe: Record<string, unknown> | null): string | null {
  if (!probe) return null
  const parts: string[] = []
  if (probe.codec) parts.push(String(probe.codec))
  if (probe.width && probe.height) parts.push(`${probe.width}×${probe.height}`)
  if (probe.duration_sec) {
    const total = Number(probe.duration_sec)
    const mins = Math.floor(total / 60)
    const secs = Math.round(total % 60)
    parts.push(`${mins}:${String(secs).padStart(2, '0')}`)
  }
  if (probe.bitrate) parts.push(`${(Number(probe.bitrate) / 1_000_000).toFixed(1)} Mbps`)
  return parts.length ? parts.join(' · ') : null
}

const CopyValue: React.FC<{ value: string; display?: string }> = ({
  value,
  display,
}) => {
  const [copied, setCopied] = useState(false)
  const onCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    const done = () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(done, done)
    } else {
      done()
    }
  }
  return (
    <span className={styles.copyValue}>
      <span className={styles.copyText} title={value}>
        {display ?? value}
      </span>
      <button type="button" className={styles.copyBtn} onClick={onCopy}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  )
}

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className={styles.fact}>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
)

const EventList: React.FC<{ events: VodPipelineEvent[] }> = ({ events }) => {
  if (!events.length) return null
  // A new job_row_id means a retry replaced the row — that boundary is the
  // attempt boundary, since the job table only ever holds the current attempt.
  let attempt = 0
  let lastJobId: string | null | undefined
  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>Transitions</div>
      <ol className={styles.events}>
        {events.map((e, i) => {
          const isNewAttempt = e.job_row_id !== lastJobId
          if (isNewAttempt) attempt += 1
          lastJobId = e.job_row_id
          return (
            <li key={`${e.occurred_at}-${i}`} className={styles.event}>
              <span className={styles.eventWhen}>
                {formatToPacificTimeWithSeconds(e.occurred_at)}
              </span>
              <span className={styles.eventBody}>
                <span className={styles.eventStates}>
                  {e.from_state ? `${e.from_state} → ` : ''}
                  <strong>{e.to_state}</strong>
                  {isNewAttempt && attempt > 1 && (
                    <Badge variant="neutral">attempt {attempt}</Badge>
                  )}
                </span>
                <span className={styles.eventMeta}>
                  {e.actor}
                  {e.detail ? ` · ${e.detail}` : ''}
                </span>
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

interface VersionRowProps {
  entry: VodPipelineVersion
  isServing: boolean
  isOpen: boolean
  onToggle: () => void
  onRetry: (version: number) => void
  isRetrying: boolean
}

const VersionRow: React.FC<VersionRowProps> = ({
  entry,
  isServing,
  isOpen,
  onToggle,
  onRetry,
  isRetrying,
}) => {
  const { upload, packaging } = entry
  const master = masterLabel(upload)
  const pack = packagingLabel(packaging, isServing)
  const burned = Boolean(upload?.burned)
  const railClass = burned
    ? styles.railBurned
    : isServing
      ? styles.railLive
      : styles.rail
  const updated = packaging?.updated_at ?? packaging?.created_at ?? upload?.created_at

  return (
    <>
      <tr
        className={`${styles.row} ${isOpen ? styles.rowOpen : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
      >
        <td className={styles.chevCell}>
          <span className={`${styles.chev} ${isOpen ? styles.chevOpen : ''}`}>▸</span>
        </td>
        <td>
          <span className={styles.versionCell}>
            <span className={railClass} />v{entry.version}
          </span>
        </td>
        <td>
          <Badge variant={master.variant}>
            {master.spin && <span className={styles.spin} />}
            {master.text}
          </Badge>
        </td>
        <td>
          <Badge variant={pack.variant}>
            {pack.spin && <span className={styles.spin} />}
            {pack.text}
          </Badge>
        </td>
        <td className={styles.mono}>
          {packaging?.mediaconvert_job_id ?? <span className={styles.muted}>—</span>}
        </td>
        <td className={`${styles.mono} ${styles.muted}`}>
          {packaging ? packaging.queue_name.replace(/^.*-vod-/, '') : '—'}
        </td>
        <td className={`${styles.mono} ${styles.muted}`}>
          {updated ? formatToPacificTime(updated) : '—'}
        </td>
      </tr>
      {isOpen && (
        <tr className={styles.detailRow}>
          <td colSpan={7}>
            <div className={styles.detail}>
              <div className={styles.section}>
                <div className={styles.sectionHead}>
                  Debug handles — kept for the life of the row
                </div>
                <dl className={styles.facts}>
                  <Fact label="Provenance">{provenanceLabel(upload)}</Fact>
                  {packaging?.mediaconvert_job_id ? (
                    <Fact label="MediaConvert job">
                      <CopyValue value={packaging.mediaconvert_job_id} />
                      <a
                        className={styles.extLink}
                        href={`https://${MEDIACONVERT_REGION}.console.aws.amazon.com/mediaconvert/home?region=${MEDIACONVERT_REGION}#/jobs/summary/${packaging.mediaconvert_job_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Console ↗
                      </a>
                    </Fact>
                  ) : (
                    <Fact label="MediaConvert job">
                      <span className={styles.muted}>
                        {packaging ? 'not yet submitted' : 'none — never submitted'}
                      </span>
                    </Fact>
                  )}
                  {packaging && <Fact label="Queue (lane)">{packaging.queue_name}</Fact>}
                  {packaging && (
                    <Fact label="Job row / request token">
                      <CopyValue value={packaging.job_row_id} />
                    </Fact>
                  )}
                  {packaging && (
                    <Fact label="Output prefix">
                      <CopyValue value={packaging.output_prefix} />
                    </Fact>
                  )}
                  {upload && (
                    <Fact label="Master">
                      <CopyValue
                        value={`s3://${upload.master_bucket}/${upload.master_key}`}
                        display={upload.master_key}
                      />
                      {upload.master_deleted && (
                        <span className={styles.muted}> · deleted</span>
                      )}
                    </Fact>
                  )}
                  {upload?.original_filename && (
                    <Fact label="Original filename">{upload.original_filename}</Fact>
                  )}
                  {upload && (
                    <Fact label="Source probe">
                      {probeSummary(upload.probe) ?? (
                        <span className={styles.muted}>—</span>
                      )}
                      {upload.declared_size_bytes
                        ? ` · ${bytes(upload.declared_size_bytes)}`
                        : ''}
                    </Fact>
                  )}
                  {upload?.attempts != null && (
                    <Fact label="Upload attempts">{upload.attempts}</Fact>
                  )}
                  {upload?.source_key && (
                    <Fact label="Delivered from">
                      <CopyValue
                        value={`s3://${upload.source_bucket}/${upload.source_key}`}
                        display={upload.source_key}
                      />
                    </Fact>
                  )}
                  {upload?.source_version_id && (
                    <Fact label="Source VersionId (pinned)">
                      <CopyValue value={upload.source_version_id} />
                    </Fact>
                  )}
                  {upload?.source_etag && (
                    <Fact label="Source ETag">{upload.source_etag}</Fact>
                  )}
                  {packaging?.backfill_batch_id && (
                    <Fact label="Backfill batch">
                      <CopyValue value={packaging.backfill_batch_id} />
                    </Fact>
                  )}
                  {packaging?.outputs_retire_at && (
                    <Fact label="Outputs retire">
                      {formatToPacificTime(packaging.outputs_retire_at)}
                    </Fact>
                  )}
                </dl>
              </div>

              {burned && (
                <p className={styles.note}>
                  This version is permanently reserved. Version numbers are never
                  reused, so the ledger keeps the gap rather than renumbering.
                  {upload?.error ? ` ${upload.error}` : ''}
                </p>
              )}

              {packaging?.state === 'ERROR' && packaging.error && (
                <p className={styles.error}>{packaging.error}</p>
              )}

              {packaging?.awaiting_reconcile && (
                <p className={styles.note}>
                  CreateJob returned ambiguously, so the job may already exist. A
                  queue-scoped reconcile resolves it — retry stays disabled here
                  because blind resubmission is what that reconcile prevents.
                </p>
              )}

              {packaging?.retryable && (
                <div className={styles.actions}>
                  <Button
                    variant="secondary"
                    onClick={(e?: React.MouseEvent) => {
                      e?.stopPropagation()
                      onRetry(entry.version)
                    }}
                    disabled={isRetrying}
                  >
                    {isRetrying ? 'Queuing…' : 'Retry packaging'}
                  </Button>
                </div>
              )}

              <EventList events={entry.events} />
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

interface VodPipelinePanelProps {
  pipeline: VodPipeline | null | undefined
  /** Asset-level origin, used for the no-pipeline explanation. */
  origin?: string
  onRetry: (version: number) => void
  isRetrying: boolean
}

/**
 * Per-version pipeline history for one asset. Placed as a full-width band below
 * the two detail columns: the collapsed ledger needs more width than a flex:1
 * column gives it, and the MediaConvert id is the first thing to elide.
 */
const VodPipelinePanel: React.FC<VodPipelinePanelProps> = ({
  pipeline,
  origin,
  onRetry,
  isRetrying,
}) => {
  const [openVersion, setOpenVersion] = useState<number | null>(null)

  if (!pipeline || pipeline.versions.length === 0) {
    return (
      <Panel margin="none">
        <Panel.Header>
          <h2>Pipeline</h2>
          <Badge variant="neutral">No pipeline</Badge>
        </Panel.Header>
        <div className={styles.emptyBody}>
          <p>
            This asset never entered the packaging pipeline — it has no master and
            no encode history.
          </p>
          <p className={styles.muted}>
            {origin === 'AMAGI'
              ? 'Amagi delivers finished HLS and DASH manifests from their own CDN, so there is nothing for us to encode.'
              : 'Assets ingested as a ready-made rendition (legacy WSC catalog, virtual assets) carry their renditions directly.'}
          </p>
        </div>
      </Panel>
    )
  }

  const { serving_version: serving, latest_version: latest } = pipeline
  const latestEntry = pipeline.versions[0]
  const latestPack = latestEntry?.packaging
  const inFlight =
    latestPack &&
    ['PENDING', 'SUBMITTED', 'PROGRESSING'].includes(latestPack.state)

  return (
    <Panel margin="none">
      <Panel.Header>
        <h2>Pipeline</h2>
        {inFlight && (
          <Badge variant="secondary">
            <span className={styles.spin} />
            Packaging v{latest}
          </Badge>
        )}
        <Badge variant="neutral">
          {latestEntry?.upload?.source_vendor
            ? `${latestEntry.upload.provenance} delivery`
            : 'First-party upload'}
        </Badge>
        <span className={styles.headerNote}>
          {serving
            ? inFlight
              ? `Serving v${serving} until v${latest} completes`
              : `Serving v${serving}`
            : 'Nothing playable yet'}
        </span>
      </Panel.Header>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th aria-label="expand" />
              <th>Version</th>
              <th>Master</th>
              <th>Packaging</th>
              <th>MediaConvert job</th>
              <th>Lane</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {pipeline.versions.map((entry) => (
              <VersionRow
                key={entry.version}
                entry={entry}
                isServing={entry.version === serving}
                isOpen={openVersion === entry.version}
                onToggle={() =>
                  setOpenVersion(openVersion === entry.version ? null : entry.version)
                }
                onRetry={onRetry}
                isRetrying={isRetrying}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

export default VodPipelinePanel
